import React, { lazy, Suspense } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import './index.css';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { initTraceUp } from '@/utils/traceup/traceup';
import { reloadOnceForStaleChunk } from '@/utils/staleChunkReload';
import { isBookingHost, readBookingHostOptionsFromEnv } from '@/modules/public-booking/utils/bookingHost';

// Spec 056 (research D15) — App autenticada y sitio público de reservas son
// chunks separados: en un host de reservas no se descarga el bundle de la app
// y viceversa.
const App = lazy(() => import('./App'));
const BookingApp = lazy(() => import('@/modules/public-booking/BookingApp'));

// Spec 027 — captura de errores globales (window.error / unhandledrejection).
// No-op salvo que VITE_TRACEUP_ENABLED=true y haya hash.
initTraceUp();

// Chunk obsoleto tras deploy: Vite emite 'vite:preloadError' cuando falla la
// precarga de un chunk/CSS dinámico (hash viejo borrado por el deploy). Se
// recarga una sola vez para traer los assets vigentes (guard anti-bucle).
window.addEventListener('vite:preloadError', (event) => {
  event.preventDefault();
  reloadOnceForStaleChunk();
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
    },
  },
});

// Rama ANTES del router: /reservar/* y /cita/* en cualquier host ⇒ BookingApp
// (con su propio BrowserRouter). Por HOST de negocio solo con
// VITE_BOOKING_ONLINE_ENABLED=true (ver bookingHost.ts). Todo lo demás
// (incluidas /r/:codigo, /v, /cert, /recuperar…) sigue en la app autenticada.
const renderBookingApp = isBookingHost(
  window.location.hostname,
  window.location.pathname,
  readBookingHostOptionsFromEnv(),
);

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    {renderBookingApp ? (
      <ErrorBoundary>
        <Suspense fallback={null}>
          <BookingApp />
        </Suspense>
      </ErrorBoundary>
    ) : (
      <QueryClientProvider client={queryClient}>
        <ErrorBoundary>
          <Suspense fallback={null}>
            <BrowserRouter>
              <App />
            </BrowserRouter>
          </Suspense>
        </ErrorBoundary>
      </QueryClientProvider>
    )}
  </React.StrictMode>,
);
